import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";

const API_BASE = (process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "");

// ─── Types ────────────────────────────────────────────────────────────────────

interface Review {
  reviewerName: string;
  rating: number;
  text: string;
  title?: string;
  featured?: boolean;
  createdAt: string;
}

interface ReviewsResponse {
  reviews: Review[];
  total: number;
  averageRating: number;
  googlePlaceId: string | null;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function timeAgo(date: string): string {
  const seconds = Math.floor((Date.now() - new Date(date).getTime()) / 1000);
  if (seconds < 60) return "just now";
  if (seconds < 3600) return Math.floor(seconds / 60) + " min ago";
  if (seconds < 86400) return Math.floor(seconds / 3600) + "h ago";
  if (seconds < 2592000) return Math.floor(seconds / 86400) + " days ago";
  if (seconds < 31536000) return Math.floor(seconds / 2592000) + " months ago";
  return Math.floor(seconds / 31536000) + " years ago";
}

function getInitial(name: string): string {
  return (name.charAt(0) || "?").toUpperCase();
}

// ─── Star Components ──────────────────────────────────────────────────────────

function StarIcon({ filled, color, size = 18 }: { filled: boolean; color: string; size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill={filled ? color : "none"}
      stroke={filled ? color : "#D1D5DB"}
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ flexShrink: 0 }}
    >
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
  );
}

function StarRating({ rating, color, size = 18 }: { rating: number; color: string; size?: number }) {
  return (
    <div style={{ display: "flex", gap: 2, alignItems: "center" }}>
      {[1, 2, 3, 4, 5].map((star) => (
        <StarIcon key={star} filled={star <= rating} color={color} size={size} />
      ))}
    </div>
  );
}

function InteractiveStarRating({
  value,
  onChange,
  color,
}: {
  value: number;
  onChange: (v: number) => void;
  color: string;
}) {
  const [hover, setHover] = useState(0);

  return (
    <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          onClick={() => onChange(star)}
          onMouseEnter={() => setHover(star)}
          onMouseLeave={() => setHover(0)}
          style={{
            background: "none",
            border: "none",
            cursor: "pointer",
            padding: 2,
            transition: "transform 0.15s ease",
            transform: (hover || value) >= star ? "scale(1.15)" : "scale(1)",
          }}
          aria-label={`Rate ${star} star${star > 1 ? "s" : ""}`}
        >
          <StarIcon
            filled={(hover || value) >= star}
            color={color}
            size={36}
          />
        </button>
      ))}
    </div>
  );
}

// ─── Rating Distribution Bar ─────────────────────────────────────────────────

function RatingDistribution({
  reviews,
  color,
}: {
  reviews: Review[];
  color: string;
}) {
  const distribution = useMemo(() => {
    const counts = [0, 0, 0, 0, 0];
    reviews.forEach((r) => {
      if (r.rating >= 1 && r.rating <= 5) counts[r.rating - 1]++;
    });
    const max = Math.max(...counts, 1);
    return [5, 4, 3, 2, 1].map((star) => ({
      star,
      count: counts[star - 1],
      pct: (counts[star - 1] / max) * 100,
    }));
  }, [reviews]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6, flex: 1 }}>
      {distribution.map(({ star, count, pct }) => (
        <div
          key={star}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            fontSize: 13,
          }}
        >
          <span
            style={{
              width: 14,
              textAlign: "right",
              color: "#64748B",
              fontWeight: 500,
            }}
          >
            {star}
          </span>
          <StarIcon filled size={12} color={color} />
          <div
            style={{
              flex: 1,
              height: 8,
              borderRadius: 4,
              backgroundColor: "#F1F5F9",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                height: "100%",
                width: `${pct}%`,
                backgroundColor: color,
                borderRadius: 4,
                transition: "width 0.6s ease",
              }}
            />
          </div>
          <span
            style={{
              width: 24,
              fontSize: 12,
              color: "#94A3B8",
              textAlign: "right",
            }}
          >
            {count}
          </span>
        </div>
      ))}
    </div>
  );
}

// ─── Review Card ──────────────────────────────────────────────────────────────

function ReviewCard({ review, color }: { review: Review; color: string }) {
  return (
    <div
      style={{
        padding: "20px 24px",
        borderRadius: 12,
        border: "1px solid #E2E8F0",
        backgroundColor: "#FFFFFF",
        transition: "box-shadow 0.2s ease",
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLDivElement).style.boxShadow =
          "0 4px 12px rgba(0,0,0,0.06)";
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLDivElement).style.boxShadow = "none";
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: 14,
        }}
      >
        {/* Avatar */}
        <div
          style={{
            width: 40,
            height: 40,
            borderRadius: "50%",
            backgroundColor: color,
            color: "#FFFFFF",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontWeight: 700,
            fontSize: 16,
            flexShrink: 0,
            letterSpacing: 0.5,
          }}
        >
          {getInitial(review.reviewerName)}
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Name + Featured badge row */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              flexWrap: "wrap",
              marginBottom: 4,
            }}
          >
            <span
              style={{
                fontWeight: 600,
                fontSize: 15,
                color: "#1E293B",
              }}
            >
              {review.reviewerName}
            </span>
            {review.featured && (
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: color,
                  backgroundColor: `${color}14`,
                  padding: "2px 8px",
                  borderRadius: 999,
                  letterSpacing: 0.3,
                  textTransform: "uppercase",
                }}
              >
                Featured
              </span>
            )}
          </div>

          {/* Stars + time */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              marginBottom: 10,
            }}
          >
            <StarRating rating={review.rating} color={color} size={15} />
            <span style={{ fontSize: 12, color: "#94A3B8" }}>
              {timeAgo(review.createdAt)}
            </span>
          </div>

          {/* Title */}
          {review.title && (
            <p
              style={{
                fontWeight: 600,
                fontSize: 14,
                color: "#334155",
                margin: "0 0 6px",
              }}
            >
              {review.title}
            </p>
          )}

          {/* Text */}
          <p
            style={{
              fontSize: 14,
              lineHeight: 1.6,
              color: "#475569",
              margin: 0,
            }}
          >
            {review.text}
          </p>
        </div>
      </div>
    </div>
  );
}

// ─── Review Gate (Leave a Review flow) ────────────────────────────────────────

type GateStep = "idle" | "rating" | "thanks" | "redirecting";

function ReviewGate({
  googlePlaceId,
  color,
}: {
  googlePlaceId: string | null;
  color: string;
}) {
  const [step, setStep] = useState<GateStep>("idle");
  const [selectedRating, setSelectedRating] = useState(0);

  const handleRate = (rating: number) => {
    setSelectedRating(rating);
    if (rating === 5) {
      // 5 stars → redirect to Google Reviews
      setStep("redirecting");
      if (googlePlaceId) {
        setTimeout(() => {
          window.open(
            `https://search.google.com/local/writereview?placeid=${encodeURIComponent(googlePlaceId)}`,
            "_blank",
            "noopener"
          );
          // Reset after a moment so they can rate again if needed
          setTimeout(() => {
            setStep("idle");
            setSelectedRating(0);
          }, 2000);
        }, 600);
      } else {
        // No Google place connected — just show thanks
        setStep("thanks");
      }
    } else {
      // Less than 5 stars → thanks message
      setStep("thanks");
    }
  };

  const handleReset = () => {
    setStep("idle");
    setSelectedRating(0);
  };

  // Idle: just the button
  if (step === "idle") {
    return (
      <div style={{ textAlign: "center" }}>
        <button
          onClick={() => setStep("rating")}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            padding: "12px 32px",
            fontSize: 15,
            fontWeight: 700,
            color: "#FFFFFF",
            backgroundColor: color,
            border: "none",
            borderRadius: 10,
            cursor: "pointer",
            transition: "all 0.2s ease",
            letterSpacing: 0.3,
          }}
          onMouseEnter={(e) => {
            (e.target as HTMLButtonElement).style.opacity = "0.9";
            (e.target as HTMLButtonElement).style.transform = "translateY(-1px)";
          }}
          onMouseLeave={(e) => {
            (e.target as HTMLButtonElement).style.opacity = "1";
            (e.target as HTMLButtonElement).style.transform = "translateY(0)";
          }}
        >
          <svg
            width={18}
            height={18}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
          </svg>
          Leave a Review
        </button>
      </div>
    );
  }

  // Rating: star selector
  if (step === "rating") {
    return (
      <div
        style={{
          padding: "28px 24px",
          borderRadius: 14,
          border: "1px solid #E2E8F0",
          backgroundColor: "#FAFBFC",
          textAlign: "center",
        }}
      >
        <h3
          style={{
            fontSize: 18,
            fontWeight: 700,
            color: "#1E293B",
            margin: "0 0 6px",
          }}
        >
          How was your experience?
        </h3>
        <p
          style={{
            fontSize: 14,
            color: "#64748B",
            margin: "0 0 20px",
          }}
        >
          Select a rating to continue
        </p>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            marginBottom: 16,
          }}
        >
          <InteractiveStarRating
            value={selectedRating}
            onChange={handleRate}
            color={color}
          />
        </div>
        <button
          onClick={handleReset}
          style={{
            background: "none",
            border: "none",
            color: "#94A3B8",
            fontSize: 13,
            cursor: "pointer",
            padding: "4px 8px",
          }}
        >
          Cancel
        </button>
      </div>
    );
  }

  // Redirecting: brief message before Google opens
  if (step === "redirecting") {
    return (
      <div
        style={{
          padding: "32px 24px",
          borderRadius: 14,
          border: "1px solid #E2E8F0",
          backgroundColor: "#FAFBFC",
          textAlign: "center",
        }}
      >
        <div
          style={{
            width: 52,
            height: 52,
            borderRadius: "50%",
            backgroundColor: `${color}14`,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            marginBottom: 14,
          }}
        >
          {/* Google icon */}
          <svg width={26} height={26} viewBox="0 0 48 48">
            <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
            <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
            <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
            <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
          </svg>
        </div>
        <h3
          style={{
            fontSize: 18,
            fontWeight: 700,
            color: "#1E293B",
            margin: "0 0 8px",
          }}
        >
          Thank you!
        </h3>
        <p style={{ fontSize: 14, color: "#64748B", margin: 0 }}>
          Taking you to Google Reviews...
        </p>
      </div>
    );
  }

  // Thanks: feedback acknowledged (less than 5 stars)
  return (
    <div
      style={{
        padding: "32px 24px",
        borderRadius: 14,
        border: "1px solid #E2E8F0",
        backgroundColor: "#FAFBFC",
        textAlign: "center",
      }}
    >
      <div
        style={{
          width: 52,
          height: 52,
          borderRadius: "50%",
          backgroundColor: `${color}14`,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          marginBottom: 14,
        }}
      >
        <svg
          width={26}
          height={26}
          viewBox="0 0 24 24"
          fill="none"
          stroke={color}
          strokeWidth={2.5}
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="20 6 9 17 4 12" />
        </svg>
      </div>
      <h3
        style={{
          fontSize: 18,
          fontWeight: 700,
          color: "#1E293B",
          margin: "0 0 8px",
        }}
      >
        Thank you for your feedback!
      </h3>
      <p style={{ fontSize: 14, color: "#64748B", margin: "0 0 18px" }}>
        We appreciate you taking the time to share your experience.
      </p>
      <button
        onClick={handleReset}
        style={{
          background: "none",
          border: `1px solid #E2E8F0`,
          color: "#64748B",
          fontSize: 13,
          fontWeight: 500,
          cursor: "pointer",
          padding: "8px 20px",
          borderRadius: 8,
          transition: "all 0.2s ease",
        }}
        onMouseEnter={(e) => {
          (e.target as HTMLButtonElement).style.backgroundColor = "#F8FAFC";
        }}
        onMouseLeave={(e) => {
          (e.target as HTMLButtonElement).style.backgroundColor = "transparent";
        }}
      >
        Done
      </button>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function EmbedReviewsPage() {
  const { firmSlug } = useParams<{ firmSlug: string }>();
  const [searchParams] = useSearchParams();
  const primaryColor = searchParams.get("color")
    ? `#${searchParams.get("color")!.replace(/^#/, "")}`
    : "#0F5132";

  const containerRef = useRef<HTMLDivElement>(null);

  // Data state
  const [reviews, setReviews] = useState<Review[]>([]);
  const [total, setTotal] = useState(0);
  const [averageRating, setAverageRating] = useState(0);
  const [googlePlaceId, setGooglePlaceId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Show more
  const INITIAL_COUNT = 3;
  const [showAll, setShowAll] = useState(false);
  const visibleReviews = showAll ? reviews : reviews.slice(0, INITIAL_COUNT);

  // ── Fetch reviews ───────────────────────────────────────────────────────────

  const fetchReviews = useCallback(async () => {
    if (!firmSlug) return;
    try {
      setLoading(true);
      const res = await axios.get<ReviewsResponse>(
        `${API_BASE}/widget/${firmSlug}/reviews`
      );
      setReviews(res.data.reviews || []);
      setTotal(res.data.total || 0);
      setAverageRating(res.data.averageRating || 0);
      setGooglePlaceId(res.data.googlePlaceId || null);
    } catch {
      setError("Unable to load reviews.");
    } finally {
      setLoading(false);
    }
  }, [firmSlug]);

  useEffect(() => {
    fetchReviews();
  }, [fetchReviews]);

  // ── Auto-resize postMessage ─────────────────────────────────────────────────

  useEffect(() => {
    const frameId = searchParams.get("frameId") || "kavoa-reviews";
    const sendHeight = () => {
      window.parent.postMessage(
        {
          type: "kavoa-resize",
          height: document.body.scrollHeight,
          id: frameId,
        },
        "*"
      );
    };

    const observer = new ResizeObserver(sendHeight);
    if (containerRef.current) observer.observe(containerRef.current);
    sendHeight();

    return () => observer.disconnect();
  }, [searchParams]);

  // ── Render ──────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 200,
          fontFamily:
            '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        }}
      >
        <div
          style={{
            width: 32,
            height: 32,
            border: `3px solid ${primaryColor}30`,
            borderTopColor: primaryColor,
            borderRadius: "50%",
            animation: "kavoa-spin 0.8s linear infinite",
          }}
        />
        <style>{`@keyframes kavoa-spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          textAlign: "center",
          padding: 40,
          color: "#94A3B8",
          fontSize: 14,
          fontFamily:
            '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        }}
      >
        {error}
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      style={{
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        maxWidth: 640,
        margin: "0 auto",
        padding: "24px 16px",
        color: "#1E293B",
        lineHeight: 1.5,
      }}
    >
      {/* ── Summary Header ──────────────────────────────────────────────────── */}
      <div
        style={{
          display: "flex",
          gap: 28,
          alignItems: "flex-start",
          marginBottom: 28,
          flexWrap: "wrap",
        }}
      >
        {/* Left: Big number + stars */}
        <div style={{ textAlign: "center", minWidth: 100 }}>
          <div
            style={{
              fontSize: 48,
              fontWeight: 700,
              color: "#1E293B",
              lineHeight: 1,
              letterSpacing: -1,
            }}
          >
            {averageRating.toFixed(1)}
          </div>
          <div style={{ margin: "8px 0 4px", display: "flex", justifyContent: "center" }}>
            <StarRating
              rating={Math.round(averageRating)}
              color={primaryColor}
              size={20}
            />
          </div>
          <div style={{ fontSize: 13, color: "#64748B" }}>
            Based on {total} review{total !== 1 ? "s" : ""}
          </div>
        </div>

        {/* Right: Distribution */}
        <div style={{ flex: 1, minWidth: 180 }}>
          <RatingDistribution reviews={reviews} color={primaryColor} />
        </div>
      </div>

      {/* ── Divider ─────────────────────────────────────────────────────────── */}
      <div
        style={{
          height: 1,
          backgroundColor: "#F1F5F9",
          marginBottom: 24,
        }}
      />

      {/* ── Reviews List ────────────────────────────────────────────────────── */}
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {visibleReviews.length === 0 && (
          <div
            style={{
              textAlign: "center",
              padding: "32px 0",
              color: "#94A3B8",
              fontSize: 14,
            }}
          >
            No reviews yet. Be the first to share your experience!
          </div>
        )}

        {visibleReviews.map((review, i) => (
          <ReviewCard key={i} review={review} color={primaryColor} />
        ))}
      </div>

      {/* ── Show More ───────────────────────────────────────────────────────── */}
      {reviews.length > INITIAL_COUNT && !showAll && (
        <div style={{ textAlign: "center", marginTop: 16 }}>
          <button
            onClick={() => setShowAll(true)}
            style={{
              background: "none",
              border: `1px solid ${primaryColor}`,
              color: primaryColor,
              padding: "10px 28px",
              borderRadius: 10,
              fontSize: 14,
              fontWeight: 600,
              cursor: "pointer",
              transition: "all 0.2s ease",
            }}
            onMouseEnter={(e) => {
              (e.target as HTMLButtonElement).style.backgroundColor = `${primaryColor}0A`;
            }}
            onMouseLeave={(e) => {
              (e.target as HTMLButtonElement).style.backgroundColor = "transparent";
            }}
          >
            Show all {reviews.length} reviews
          </button>
        </div>
      )}

      {/* ── Divider ─────────────────────────────────────────────────────────── */}
      <div
        style={{
          height: 1,
          backgroundColor: "#F1F5F9",
          margin: "28px 0",
        }}
      />

      {/* ── Leave a Review (Star Gate) ─────────────────────────────────────── */}
      <ReviewGate googlePlaceId={googlePlaceId} color={primaryColor} />
    </div>
  );
}
